import type * as THREE from 'three'
import type {
  InputConsumer,
  NormalizedKeyEvent,
  NormalizedPointerEvent,
  NormalizedWheelEvent,
} from '@/shared/types/core'
import type {
  InputRouter,
  ViewEventRouter as IViewEventRouter,
  ViewInputConsumer,
  ViewKeyEvent,
  ViewPointerEvent,
  ViewRegistry,
  ViewWheelEvent,
} from '@/shared/types/services'
import type { ViewId, ViewSnapshot } from '@/shared/types/view'
import { screenToViewNDC, toViewportLocalPoint } from '@/shared/utils/viewRect'

const VIEW_EVENT_ROUTER_PRIORITY = 20_000

/**
 * 多视口输入路由，将容器级输入事件派发到命中的视图 consumer。
 */
export class ViewEventRouter implements IViewEventRouter {
  private readonly consumers: ViewInputConsumer[] = []
  private readonly inputConsumer: InputConsumer
  private inputRouter: InputRouter | null = null
  private viewRegistry: ViewRegistry | null = null
  private containerViewportProvider:
    | (() => { x: number; y: number; width: number; height: number })
    | null = null
  private capturedViewIdValue: ViewId | null = null
  private disposed = false

  constructor(inputRouter?: InputRouter, viewRegistry?: ViewRegistry) {
    this.inputConsumer = {
      name: 'view-event-router',
      priority: VIEW_EVENT_ROUTER_PRIORITY,
      onPointerDown: (event) => this.handlePointerDown(event),
      onPointerMove: (event) => this.handlePointerMove(event),
      onPointerUp: (event) => this.handlePointerUp(event),
      onWheel: (event) => this.handleWheel(event),
      onKeyDown: (event) => this.handleKeyDown(event),
      onKeyUp: (event) => this.handleKeyUp(event),
      onWinKeyDown: (event) => this.handleWinKeyDown(event),
      onWinKeyUp: (event) => this.handleWinKeyUp(event),
      onDoubleClick: (event) => this.handleDoubleClick(event),
    }
    if (inputRouter && viewRegistry) {
      this.connect(inputRouter, viewRegistry)
    }
  }

  /** 当前活动视图 ID */
  get activeViewId(): ViewId | null {
    return this.viewRegistry?.getActive()?.id ?? null
  }

  /** 当前捕获指针的视图 ID（按下期间锁定） */
  get capturedViewId(): ViewId | null {
    return this.getCapturedViewSnapshot()?.id ?? null
  }

  /**
   * 注册视图级输入消费者。
   *
   * @param consumer - 视图级输入消费者
   */
  addConsumer(consumer: ViewInputConsumer): void {
    this.consumers.push(consumer)
    this.sortConsumers()
  }

  /**
   * 移除视图级输入消费者。
   *
   * @param consumer - 视图级输入消费者；不存在时忽略
   */
  removeConsumer(consumer: ViewInputConsumer): void {
    const index = this.consumers.indexOf(consumer)
    if (index >= 0) {
      this.consumers.splice(index, 1)
    }
  }

  /**
   * 连接 InputRouter 与 ViewRegistry，开始监听输入。
   *
   * @param inputRouter - 容器级输入路由
   * @param viewRegistry - 视图注册表
   * @param containerViewportProvider - 获取容器视口矩形的回调
   */
  connect(
    inputRouter: InputRouter,
    viewRegistry: ViewRegistry,
    containerViewportProvider?: () => {
      x: number
      y: number
      width: number
      height: number
    }
  ): void {
    if (this.disposed) {
      throw new Error('ViewEventRouter 已销毁，无法重新连接')
    }

    if (this.inputRouter) {
      this.inputRouter.removeConsumer(this.inputConsumer)
    }

    this.inputRouter = inputRouter
    this.viewRegistry = viewRegistry
    this.containerViewportProvider = containerViewportProvider ?? null
    this.inputRouter.addConsumer(this.inputConsumer)
  }

  /** 释放路由，取消订阅并清理消费者 */
  dispose(): void {
    if (this.disposed) {
      return
    }

    this.disposed = true
    this.capturedViewIdValue = null
    this.consumers.length = 0
    this.inputRouter?.removeConsumer(this.inputConsumer)
    this.inputRouter = null
    this.viewRegistry = null
    this.containerViewportProvider = null
  }

  private sortConsumers(): void {
    this.consumers.sort((left, right) => right.priority - left.priority)
  }

  private handlePointerDown(event: NormalizedPointerEvent): boolean {
    const view = this.resolveHitView(event)
    if (!view) {
      return false
    }

    this.capturedViewIdValue = view.id
    return this.dispatchPointer('onPointerDown', event, view, false)
  }

  private handlePointerMove(event: NormalizedPointerEvent): boolean {
    const routed = this.resolvePointerRoute(event)
    if (!routed) {
      return false
    }

    return this.dispatchPointer('onPointerMove', event, routed.view, routed.captured)
  }

  private handlePointerUp(event: NormalizedPointerEvent): boolean {
    const hadCapture = this.capturedViewIdValue !== null
    const capturedView = this.getCapturedViewSnapshot()
    if (capturedView) {
      try {
        return this.dispatchPointer('onPointerUp', event, capturedView, true)
      } finally {
        this.capturedViewIdValue = null
      }
    }

    if (hadCapture) {
      // 捕获 view 已失效：不回退路由到命中 view，但放行事件给低优先级
      // 原始 consumer——它们可能在 down 未被消费时收到过 down，吞掉 up 会使手势悬挂
      this.capturedViewIdValue = null
      return false
    }

    const hitView = this.resolveHitView(event)
    if (!hitView) {
      return false
    }
    try {
      return this.dispatchPointer('onPointerUp', event, hitView, false)
    } finally {
      this.capturedViewIdValue = null
    }
  }

  private handleWheel(event: NormalizedWheelEvent): boolean {
    const routed = this.resolveWheelRoute(event)
    if (!routed) {
      return false
    }

    const routedEvent = this.createWheelEvent(event, routed.view, routed.captured)
    return this.dispatchWheel(routedEvent)
  }

  private handleKeyDown(event: NormalizedKeyEvent): boolean {
    const routedEvent = this.createKeyEvent(event)
    if (!routedEvent) {
      return false
    }

    return this.dispatchKey('onKeyDown', routedEvent)
  }

  private handleKeyUp(event: NormalizedKeyEvent): boolean {
    const routedEvent = this.createKeyEvent(event)
    if (!routedEvent) {
      return false
    }

    return this.dispatchKey('onKeyUp', routedEvent)
  }

  private handleWinKeyDown(event: NormalizedKeyEvent): boolean {
    const routedEvent = this.createKeyEvent(event)
    if (!routedEvent) {
      return false
    }

    return this.dispatchKey('onWinKeyDown', routedEvent)
  }

  private handleWinKeyUp(event: NormalizedKeyEvent): boolean {
    const routedEvent = this.createKeyEvent(event)
    if (!routedEvent) {
      return false
    }

    return this.dispatchKey('onWinKeyUp', routedEvent)
  }

  private handleDoubleClick(event: NormalizedPointerEvent): boolean {
    const routed = this.resolveWheelRoute(event)
    if (!routed) {
      return false
    }

    return this.dispatchPointer('onDoubleClick', event, routed.view, routed.captured)
  }

  private resolvePointerRoute(event: Pick<NormalizedPointerEvent, 'screen'>): {
    view: ViewSnapshot
    captured: boolean
  } | null {
    const capturedView = this.getCapturedViewSnapshot()
    if (capturedView) {
      return { view: capturedView, captured: true }
    }

    const hitView = this.resolveHitView(event)
    if (!hitView) {
      return null
    }

    return { view: hitView, captured: false }
  }

  private resolveWheelRoute(event: Pick<NormalizedPointerEvent | NormalizedWheelEvent, 'screen'>): {
    view: ViewSnapshot
    captured: boolean
  } | null {
    return this.resolvePointerRoute(event as Pick<NormalizedPointerEvent, 'screen'>)
  }

  private resolveHitView(
    event: Pick<NormalizedPointerEvent | NormalizedWheelEvent, 'screen'>
  ): ViewSnapshot | null {
    return this.viewRegistry?.resolveViewAtScreen(this.toContainerLocalScreen(event.screen)) ?? null
  }

  private getCapturedViewSnapshot(): ViewSnapshot | null {
    if (!this.capturedViewIdValue) {
      return null
    }

    const snapshot = this.viewRegistry?.get(this.capturedViewIdValue)
    if (!snapshot || !snapshot.visible || snapshot.rect.width <= 0 || snapshot.rect.height <= 0) {
      this.capturedViewIdValue = null
      return null
    }

    return snapshot
  }

  private createPointerEvent(
    event: NormalizedPointerEvent,
    view: ViewSnapshot,
    captured: boolean
  ): ViewPointerEvent {
    const screen = this.toContainerLocalScreen(event.screen)
    return {
      ...event,
      screen,
      viewport: { ...view.rect },
      viewId: view.id,
      viewRect: { ...view.rect },
      localNDC: screenToViewNDC(screen, view.rect),
      captured,
    }
  }

  private createWheelEvent(
    event: NormalizedWheelEvent,
    view: ViewSnapshot,
    captured: boolean
  ): ViewWheelEvent {
    const screen = this.toContainerLocalScreen(event.screen)
    return {
      ...event,
      screen,
      viewport: { ...view.rect },
      viewId: view.id,
      viewRect: { ...view.rect },
      localNDC: screenToViewNDC(screen, view.rect),
      captured,
    }
  }

  private createKeyEvent(event: NormalizedKeyEvent): ViewKeyEvent | null {
    const activeViewId = this.activeViewId
    if (!activeViewId) {
      return null
    }

    return {
      ...event,
      viewId: activeViewId,
      captured: this.capturedViewId === activeViewId,
    }
  }

  private dispatchPointer(
    methodName: 'onPointerDown' | 'onPointerMove' | 'onPointerUp' | 'onDoubleClick',
    event: NormalizedPointerEvent,
    view: ViewSnapshot,
    captured: boolean
  ): boolean {
    const routedEvent = this.createPointerEvent(event, view, captured)
    for (const consumer of this.consumers) {
      const handler = consumer[methodName]
      if (handler?.call(consumer, routedEvent)) {
        return true
      }
    }
    return false
  }

  private dispatchWheel(event: ViewWheelEvent): boolean {
    for (const consumer of this.consumers) {
      if (consumer.onWheel?.(event)) {
        return true
      }
    }
    return false
  }

  private dispatchKey(
    methodName: 'onKeyDown' | 'onKeyUp' | 'onWinKeyDown' | 'onWinKeyUp',
    event: ViewKeyEvent
  ): boolean {
    for (const consumer of this.consumers) {
      const handler = consumer[methodName]
      if (handler?.call(consumer, event)) {
        return true
      }
    }
    return false
  }

  private toContainerLocalScreen(screen: THREE.Vector2): THREE.Vector2 {
    const viewport = this.containerViewportProvider?.()
    if (!viewport) {
      return screen.clone()
    }

    return toViewportLocalPoint(screen, viewport)
  }
}
